import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatEUR, firstCategoryWithProducts } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Receipt, Plus, Minus, Bike, ShoppingBag, UtensilsCrossed, Phone, MapPin, Clock, ChefHat, CheckCircle2, PackageCheck, X, StickyNote } from "lucide-react";
import ScontrinoPreview from "@/components/ScontrinoPreview";
import LotteryCodeInput from "@/components/LotteryCodeInput";
import InlinePOSKeypad from "@/components/pos/InlinePOSKeypad";
import GestionaleLongPress from "@/components/pos/GestionaleLongPress";
import PrecontoButton from "@/components/pos/PrecontoButton";
import { printComandeViaQz, thermalPrintError } from "@/lib/printService";
import { authorizeCardIfNeeded } from "@/lib/paymentPos";
import { emitFiscalPaper } from "@/lib/fiscal";
import { newOfflineId } from "@/lib/offlineQueue";
import { recoverUnreachableSale, SERVER_ATTEMPT } from "@/lib/offlineSales";

const DELIVERY_TYPES = [
  { id: "tavolo", label: "Tavolo", icon: UtensilsCrossed, color: "#1E2D5B" },
  { id: "asporto", label: "Asporto", icon: ShoppingBag, color: "#DC2626" },
  { id: "consegna", label: "Consegna", icon: Bike, color: "#2F9E77" },
];

const STATUS_FLOW = {
  preparing: { next: "ready", label: "In preparazione", color: "#F59E0B", icon: ChefHat, nextLabel: "Pronto" },
  ready: { next: "delivered", label: "Pronto", color: "#2F9E77", icon: PackageCheck, nextLabel: "Consegnato" },
  delivered: { next: null, label: "Consegnato", color: "#94A3B8", icon: CheckCircle2, nextLabel: null },
};

export default function POSPizzeria() {
  const [tenant, setTenant] = useState(null);
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [lines, setLines] = useState([]);
  const [deliveryType, setDeliveryType] = useState("asporto");
  const [customer, setCustomer] = useState({ name: "", phone: "", address: "", pickup_time: "", notes: "" });
  const [showPay, setShowPay] = useState(false);
  const [last, setLast] = useState(null);
  const [showScontrino, setShowScontrino] = useState(false);
  const [lotteryCode, setLotteryCode] = useState("");
  const [showOrders, setShowOrders] = useState(false);
  const [openOrders, setOpenOrders] = useState([]);
  const [discountPct, setDiscountPct] = useState(0);
  const [noteIdx, setNoteIdx] = useState(null);
  const chips = useMemo(() => (tenant?.line_note_suggestions?.length ? tenant.line_note_suggestions : [
    { label: "ben cotta", price_delta: 0 }, { label: "poco cotta", price_delta: 0 },
    { label: "senza mozzarella", price_delta: -1 }, { label: "extra bufala", price_delta: 2 },
    { label: "senza glutine", price_delta: 0 }, { label: "senza lattosio", price_delta: 0 },
  ]), [tenant]);
  const updateLineNote = (idx, note) => {
    const upd = [...lines]; upd[idx] = { ...upd[idx], note: note || null }; setLines(upd);
  };
  const applyChip = (idx, chip) => {
    if (!chip.label) return;
    const upd = [...lines]; const cur = { ...upd[idx] };
    if (!chip.price_delta || chip.price_delta === 0) cur.note = chip.label;
    else {
      const exs = (Array.isArray(cur.extras) ? cur.extras : []).filter((e) => e.name !== chip.label);
      exs.push({ name: chip.label, price: chip.price_delta });
      cur.extras = exs;
    }
    upd[idx] = cur; setLines(upd);
  };

  useEffect(() => {
    api.get("/tenant/me").then((r) => setTenant(r.data));
    api.get("/categories?mode=pizzeria").then((r) => setCats(Array.isArray(r.data) ? r.data : []));
    api.get("/products?mode=pizzeria").then((r) => setProds(Array.isArray(r.data) ? r.data : []));
    loadOpenOrders();
    const t = setInterval(loadOpenOrders, 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (activeCat == null && cats.length && prods.length) setActiveCat(firstCategoryWithProducts(cats, prods));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cats, prods]);

  const loadOpenOrders = () => {
    api.get("/orders?mode=pizzeria&limit=50").then(r => {
      const active = (Array.isArray(r.data) ? r.data : []).filter(o => o.delivery && o.delivery.delivery_status && o.delivery.delivery_status !== 'delivered');
      setOpenOrders(active);
    }).catch(() => {});
  };

  const filtered = useMemo(() => (Array.isArray(prods) ? prods : []).filter(p => p.category_id === activeCat && p.visible !== false), [prods, activeCat]);
  const subtotal = lines.reduce((s, l) => s + l.price * l.qty + (l.extras || []).reduce((ss, e) => ss + parseFloat(e.price || 0), 0), 0);
  const total = discountPct > 0 ? subtotal * (1 - discountPct / 100) : subtotal;

  const addProd = (p) => {
    const i = lines.findIndex(l => l.product_id === p.id);
    if (i >= 0) { const u = [...lines]; u[i] = { ...u[i], qty: u[i].qty + 1 }; setLines(u); }
    else setLines([...lines, { product_id: p.id, name: p.name, qty: 1, price: p.price, vat: p.vat, print_to: p.effective_print_to || p.print_to, category_id: p.category_id }]);
  };

  const addFreeLine = (amountOrObj) => {
    let name, price, vat;
    if (typeof amountOrObj === "number") {
      name = "Prezzo libero"; price = amountOrObj; vat = 22;
    } else {
      ({ name, price, vat } = amountOrObj);
    }
    setLines((prev) => [...prev, { product_id: null, name, qty: 1, price, vat }]);
    toast.success(`Aggiunto: ${name} · ${formatEUR(price)}`);
  };

  const applyDiscount = ({ mode, value }) => {
    const pct = mode === "pct" ? Math.min(100, Math.max(0, value)) : (subtotal > 0 ? Math.min(100, (value / subtotal) * 100) : 0);
    setDiscountPct(pct);
    toast.success(pct > 0 ? `Sconto ${pct.toFixed(1)}% applicato` : "Sconto rimosso");
  };

  const [precontoOrderId, setPrecontoOrderId] = useState(null);
  const saveOrderForGestionale = async () => {
    if (!lines.length) { toast.error("Nessun articolo"); return null; }
    try {
      if (precontoOrderId) {
        const r = await api.patch(`/orders/${precontoOrderId}`, { lines, discount_pct: discountPct || 0 });
        return r.data;
      }
      const r = await api.post("/orders", {
        mode: "pizzeria",
        lines,
        discount_pct: discountPct || 0,
        delivery: {
          delivery_type: deliveryType,
          delivery_status: "preparing",
          customer_name: customer.name || null,
          customer_phone: customer.phone || null,
          customer_address: customer.address || null,
          pickup_time: customer.pickup_time || null,
          notes: customer.notes || null,
        },
      });
      return r.data;
    } catch { toast.error("Errore salvataggio ordine"); return null; }
  };

  const onGestionaleClosed = () => {
    setLines([]); setDiscountPct(0); setPrecontoOrderId(null);
    setCustomer({ name: "", phone: "", address: "", pickup_time: "", notes: "" });
    loadOpenOrders();
  };
  const incLine = (i, d) => {
    const u = [...lines]; u[i] = { ...u[i], qty: Math.max(0, u[i].qty + d) };
    if (u[i].qty === 0) u.splice(i, 1); setLines(u);
  };

  const validCustomer = () => {
    if (deliveryType === "tavolo") return true;
    if (deliveryType === "asporto") return !!customer.name.trim();
    if (deliveryType === "consegna") return !!customer.name.trim() && !!customer.phone.trim() && !!customer.address.trim();
    return true;
  };

  const sendToKitchen = async () => {
    if (!lines.length) return toast.error("Nessun articolo");
    if (!validCustomer()) return toast.error("Compila i dati cliente");
    try {
      const r = await api.post("/orders", {
        mode: "pizzeria",
        lines,
        discount_pct: discountPct || 0,
        delivery: {
          delivery_type: deliveryType,
          delivery_status: "preparing",
          customer_name: customer.name || null,
          customer_phone: customer.phone || null,
          customer_address: customer.address || null,
          pickup_time: customer.pickup_time || null,
          notes: customer.notes || null,
        },
      });
      toast.success("Ordine inviato in cucina!");
      printComandeViaQz({ order: r.data, tenant })
        .then((res) => {
          if (res.ok) toast.success(`Comanda stampata: ${(Array.isArray(res.printed) ? res.printed : []).map((p) => p.printer).join(", ")}`);
          else toast.error(thermalPrintError(res), { duration: 8000 });
        })
        .catch((e) => toast.error(String(e?.message || e)));
      setLines([]); setDiscountPct(0); setPrecontoOrderId(null);
      setCustomer({ name: "", phone: "", address: "", pickup_time: "", notes: "" });
      loadOpenOrders();
    } catch { toast.error("Errore invio ordine"); }
  };

  const closeWithReceipt = async (method) => {
    if (!validCustomer()) return toast.error("Compila i dati cliente");
    const pos = await authorizeCardIfNeeded({ method, amount: total });
    if (!pos.ok) { toast.error(pos.message || "Pagamento carta non autorizzato"); return; }
    if (pos.mode === "pos") toast.success("Carta autorizzata sul POS");
    const offlineId = newOfflineId();
    const delivery = {
      delivery_type: deliveryType,
      delivery_status: "delivered",
      customer_name: customer.name || null,
      customer_phone: customer.phone || null,
      customer_address: customer.address || null,
      pickup_time: customer.pickup_time || null,
    };
    let oid = precontoOrderId || null;
    try {
      if (precontoOrderId) {
        const upd = { lines, discount_pct: discountPct || 0 };
        if (lotteryCode) upd.lottery_code = lotteryCode;
        await api.patch(`/orders/${precontoOrderId}`, upd, SERVER_ATTEMPT);
        oid = precontoOrderId;
      } else {
        const r = await api.post("/orders", {
          mode: "pizzeria",
          lines,
          payment_method: method,
          discount_pct: discountPct || 0,
          lottery_code: lotteryCode || undefined,
          offline_id: offlineId,
          delivery,
        }, SERVER_ATTEMPT);
        oid = r.data.id;
      }
      const c = await api.post(`/orders/${oid}/close-fiscal`, { payment_method: method, discount_pct: discountPct || 0 }, SERVER_ATTEMPT);
      setLast(c.data); setShowPay(false); setShowScontrino(true);
      setLines([]); setDiscountPct(0); setCustomer({ name: "", phone: "", address: "", pickup_time: "", notes: "" }); setLotteryCode(""); setPrecontoOrderId(null);
      toast.success(`Scontrino #${c.data.receipt_number} (gestionale). Attendi la carta sulla Epson…`);
      await emitFiscalPaper({ tenant, order: c.data });
    } catch (e) {
      const off = await recoverUnreachableSale(e, {
        tenant, offline_id: offlineId, server_order_id: oid, mode: "pizzeria", lines,
        payment_method: method, discount_pct: discountPct || 0, lottery_code: lotteryCode || null,
        delivery, notes: customer.notes || null,
      });
      if (off.printed) {
        setLast(off.order); setShowPay(false); setShowScontrino(true);
        setLines([]); setDiscountPct(0); setCustomer({ name: "", phone: "", address: "", pickup_time: "", notes: "" }); setLotteryCode(""); setPrecontoOrderId(null);
        return;
      }
      if (!off.handled) toast.error("Errore");
    }
  };

  const advanceStatus = async (order) => {
    const cur = order.delivery?.delivery_status;
    const next = STATUS_FLOW[cur]?.next;
    if (!next) return;
    try {
      await api.patch(`/orders/${order.id}/delivery-status?delivery_status=${next}`);
      toast.success(`Stato → ${STATUS_FLOW[next].label}`);
      loadOpenOrders();
    } catch { toast.error("Errore aggiornamento"); }
  };

  return (
    <div className="h-screen bg-velta-bg flex flex-col overflow-hidden">
      <header className="h-14 border-b border-velta-border bg-white flex items-center px-4 gap-4 shrink-0">
        <Link to="/cassa" data-testid="pizza-back" className="inline-flex items-center gap-2 text-velta-mute hover:text-velta-ink text-sm font-medium">
          <ArrowLeft className="w-4 h-4" /> Modalità
        </Link>
        <div className="h-6 w-px bg-velta-border" />
        <div className="font-heading font-semibold tracking-tight">Velta One · Pizzeria</div>
        <div className="ml-auto">
          <button
            data-testid="pizza-show-orders"
            onClick={() => setShowOrders(true)}
            className="relative h-10 px-4 bg-[#DC2626] text-white rounded-md font-medium btn-tactile inline-flex items-center gap-2"
          >
            <ChefHat className="w-4 h-4" />
            Ordini in corso
            {openOrders.length > 0 && (
              <span className="bg-white text-[#DC2626] text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">{openOrders.length}</span>
            )}
          </button>
        </div>
      </header>

      <div className="flex-1 grid grid-cols-12 overflow-hidden">
        <section className="col-span-7 flex flex-col overflow-hidden">
          {/* Delivery type selector */}
          <div className="h-14 border-b border-velta-border bg-white flex items-center gap-2 px-3">
            {(Array.isArray(DELIVERY_TYPES) ? DELIVERY_TYPES : []).map(d => {
              const Icon = d.icon;
              const active = deliveryType === d.id;
              return (
                <button
                  key={d.id}
                  data-testid={`pizza-dtype-${d.id}`}
                  onClick={() => setDeliveryType(d.id)}
                  className={`h-10 px-4 rounded-md font-medium btn-tactile inline-flex items-center gap-2 ${active ? "text-white" : "bg-white border border-velta-border text-velta-ink hover:border-velta-ink"}`}
                  style={active ? { background: d.color } : {}}
                >
                  <Icon className="w-4 h-4" /> {d.label}
                </button>
              );
            })}
          </div>

          <div className="h-20 border-b border-velta-border bg-white flex gap-2 overflow-x-auto pos-scroll p-3">
            {(Array.isArray(cats) ? cats : []).map(c => (
              <button key={c.id} data-testid={`pizza-cat-${c.id}`} onClick={() => setActiveCat(c.id)}
                className={`shrink-0 h-14 px-5 rounded-md font-medium btn-tactile ${activeCat === c.id ? "text-white" : "bg-white border border-velta-border text-velta-ink hover:border-velta-ink"}`}
                style={activeCat === c.id ? { background: c.color } : {}}>{c.name}</button>
            ))}
          </div>

          <div className="flex-1 overflow-auto pos-scroll p-3">
            <div className="grid grid-cols-3 lg:grid-cols-4 gap-3">
              {(Array.isArray(filtered) ? filtered : []).map(p => {
                const cat = cats.find(c => c.id === p.category_id);
                const color = p.color || cat?.color || "#0A0A0A";
                return (
                  <button key={p.id} data-testid={`pizza-prod-${p.id}`} onClick={() => addProd(p)} className="relative bg-white border border-velta-border rounded-md p-4 h-28 text-left flex flex-col justify-between hover:border-velta-ink btn-tactile">
                    <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: color }} />
                    <div className="font-medium text-sm mt-2">{p.name}</div>
                    {p.show_price && <div className="font-mono font-bold text-lg">{formatEUR(p.price)}</div>}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <aside className="col-span-5 border-l border-velta-border bg-white flex flex-col overflow-hidden">
          {/* Customer form */}
          {deliveryType !== "tavolo" && (
            <div className="p-3 border-b border-velta-border bg-velta-bg space-y-2">
              <div className="text-xs font-mono uppercase tracking-widest text-velta-mute">Dati cliente</div>
              <input
                data-testid="pizza-cust-name"
                placeholder="Nome cliente *"
                value={customer.name}
                onChange={e => setCustomer({ ...customer, name: e.target.value })}
                className="w-full h-10 px-3 border border-velta-border rounded-md text-sm"
              />
              <div className="grid grid-cols-2 gap-2">
                <div className="relative">
                  <Phone className="w-4 h-4 absolute left-2 top-3 text-velta-mute" />
                  <input
                    data-testid="pizza-cust-phone"
                    placeholder="Telefono"
                    value={customer.phone}
                    onChange={e => setCustomer({ ...customer, phone: e.target.value })}
                    className="w-full h-10 pl-8 pr-2 border border-velta-border rounded-md text-sm"
                  />
                </div>
                <div className="relative">
                  <Clock className="w-4 h-4 absolute left-2 top-3 text-velta-mute" />
                  <input
                    data-testid="pizza-cust-time"
                    type="time"
                    placeholder="Orario"
                    value={customer.pickup_time}
                    onChange={e => setCustomer({ ...customer, pickup_time: e.target.value })}
                    className="w-full h-10 pl-8 pr-2 border border-velta-border rounded-md text-sm"
                  />
                </div>
              </div>
              {deliveryType === "consegna" && (
                <div className="relative">
                  <MapPin className="w-4 h-4 absolute left-2 top-3 text-velta-mute" />
                  <input
                    data-testid="pizza-cust-address"
                    placeholder="Indirizzo consegna *"
                    value={customer.address}
                    onChange={e => setCustomer({ ...customer, address: e.target.value })}
                    className="w-full h-10 pl-8 pr-2 border border-velta-border rounded-md text-sm"
                  />
                </div>
              )}
              <input
                data-testid="pizza-cust-notes"
                placeholder="Note (allergie, dettagli)"
                value={customer.notes}
                onChange={e => setCustomer({ ...customer, notes: e.target.value })}
                className="w-full h-10 px-3 border border-velta-border rounded-md text-sm"
              />
            </div>
          )}

          <div className="flex-1 overflow-auto pos-scroll p-3">
            {lines.length === 0 ? <div className="text-center text-sm text-velta-mute p-8">Aggiungi articoli.</div> : (
              <div className="space-y-2">
                {(Array.isArray(lines) ? lines : []).map((l, i) => (
                  <div key={i} className={`border rounded-md p-3 ${l.note || (l.extras || []).length ? "border-velta-ink" : "border-velta-border"}`}>
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{l.name}</div>
                        {l.note && noteIdx !== i && <div className="text-[11px] text-velta-ink italic">↳ {l.note}</div>}
                        {(Array.isArray(l.extras) ? l.extras : []).map((ex) => (
                          <div key={ex.name} className={`text-[10px] ${ex.price < 0 ? "text-velta-success" : "text-velta-warning"}`}>
                            {ex.price > 0 ? "+" : ""}{ex.name} ({ex.price > 0 ? "+" : ""}{formatEUR(ex.price)})
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-1">
                        <button data-testid={`pizza-line-note-${i}`} onClick={() => setNoteIdx(noteIdx === i ? null : i)} className={`h-8 w-8 rounded flex items-center justify-center ${l.note || (l.extras || []).length ? "text-velta-ink bg-blue-50" : "text-velta-mute hover:bg-velta-bg"}`} title="Nota"><StickyNote className="w-3.5 h-3.5" /></button>
                        <button onClick={() => incLine(i, -l.qty)} className="text-velta-error"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="inline-flex items-center border border-velta-border rounded-md">
                        <button onClick={() => incLine(i, -1)} className="w-8 h-8 flex items-center justify-center"><Minus className="w-3.5 h-3.5" /></button>
                        <span className="w-8 text-center font-mono font-medium">{l.qty}</span>
                        <button onClick={() => incLine(i, 1)} className="w-8 h-8 flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <div className="font-mono font-bold">{formatEUR(l.price * l.qty + (l.extras || []).reduce((s, e) => s + parseFloat(e.price || 0), 0))}</div>
                    </div>
                    {noteIdx === i && (
                      <div className="mt-2 pt-2 border-t border-velta-border/50">
                        <input
                          autoFocus
                          data-testid={`pizza-line-note-input-${i}`}
                          maxLength={80}
                          defaultValue={l.note || ""}
                          onBlur={(e) => { updateLineNote(i, e.target.value.trim()); setNoteIdx(null); }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") { updateLineNote(i, e.currentTarget.value.trim()); setNoteIdx(null); }
                            else if (e.key === "Escape") { setNoteIdx(null); }
                          }}
                          placeholder="Nota libera cucina..."
                          className="h-9 w-full bg-white border border-velta-border rounded px-2 text-[12px] outline-none focus:border-velta-ink"
                        />
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {(Array.isArray(chips) ? chips : []).map((c) => (
                            <button key={c.label}
                              data-testid={`pizza-line-note-sug-${i}-${c.label.replace(/ /g, '-')}`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => { applyChip(i, c); setNoteIdx(null); }}
                              className={`text-[10px] px-2 h-6 bg-white border rounded-full ${c.price_delta > 0 ? "border-velta-warning text-velta-warning" : c.price_delta < 0 ? "border-velta-success text-velta-success" : "border-velta-border"} hover:bg-velta-ink hover:text-white hover:border-velta-ink`}>
                              {c.label}{c.price_delta ? ` ${c.price_delta > 0 ? "+" : ""}${c.price_delta}€` : ""}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-velta-border p-3 bg-velta-bg space-y-2">
            <GestionaleLongPress
              orderId={null}
              total={total}
              saveOrder={saveOrderForGestionale}
              onClosed={onGestionaleClosed}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono uppercase tracking-widest text-velta-mute">Totale</span>
                <span data-testid="pizza-total" className="font-mono font-bold text-3xl tracking-tighter">{formatEUR(total)}</span>
              </div>
              {discountPct > 0 && (
                <div className="text-[10px] font-mono text-velta-success mt-1 text-right">
                  Sconto -{discountPct.toFixed(1)}% (subtot. {formatEUR(subtotal)})
                </div>
              )}
            </GestionaleLongPress>
            <InlinePOSKeypad
              onAddFreePrice={addFreeLine}
              onApplyDiscount={applyDiscount}
              currentTotal={total}
              compact
            />
            <PrecontoButton
              mode="pizzeria"
              lines={lines}
              discountPct={discountPct}
              tenant={tenant}
              orderId={precontoOrderId}
              onOrderSaved={setPrecontoOrderId}
              testid="pizza-preconto-btn"
              className="w-full h-11 bg-white border-2 border-velta-ink text-velta-ink hover:bg-velta-ink hover:text-white rounded-md text-sm font-medium flex items-center justify-center gap-2 btn-tactile disabled:opacity-40"
            />
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="pizza-send-kitchen"
                disabled={!lines.length}
                onClick={sendToKitchen}
                className="h-14 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-md font-medium flex items-center justify-center gap-2 btn-tactile disabled:opacity-40"
              >
                <ChefHat className="w-5 h-5" /> Cucina
              </button>
              <button
                data-testid="pizza-pay"
                disabled={!lines.length}
                onClick={() => setShowPay(true)}
                className="h-14 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium flex items-center justify-center gap-2 btn-tactile disabled:opacity-40"
              >
                <Receipt className="w-5 h-5" /> Scontrino
              </button>
            </div>
          </div>
        </aside>
      </div>

      {/* Payment modal */}
      {showPay && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setShowPay(false)}>
          <div className="bg-white rounded-md w-full max-w-md p-6 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-2xl font-semibold mb-4">Pagamento</h3>
            <div className="bg-velta-bg rounded-md p-4 mb-4 text-center"><div className="font-mono font-bold text-3xl">{formatEUR(total)}</div></div>
            <div className="mb-4"><LotteryCodeInput value={lotteryCode} onChange={setLotteryCode} testid="pizza-lottery" /></div>
            <div className="grid grid-cols-3 gap-2">
              <button data-testid="pizza-pay-cash" onClick={() => closeWithReceipt("cash")} className="h-14 bg-velta-ink text-white rounded-md font-medium btn-tactile">Contanti</button>
              <button data-testid="pizza-pay-card" onClick={() => closeWithReceipt("card")} className="h-14 bg-[#1E2D5B] text-white rounded-md font-medium btn-tactile">Carta</button>
              <button data-testid="pizza-pay-ticket" onClick={() => closeWithReceipt("ticket")} className="h-14 bg-velta-bg border border-velta-border text-velta-ink rounded-md font-medium btn-tactile">Ticket</button>
            </div>
          </div>
        </div>
      )}

      {/* Orders in progress modal */}
      {showOrders && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setShowOrders(false)}>
          <div className="bg-white rounded-md w-full max-w-4xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-velta-border flex items-center justify-between">
              <h3 className="font-heading text-xl font-semibold">Ordini in corso ({openOrders.length})</h3>
              <button onClick={() => setShowOrders(false)} data-testid="pizza-orders-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {openOrders.length === 0 ? (
                <div className="text-center text-velta-mute py-12">Nessun ordine in corso.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(Array.isArray(openOrders) ? openOrders : []).map(o => {
                    const st = STATUS_FLOW[o.delivery?.delivery_status || "preparing"];
                    const StIcon = st.icon;
                    const dtype = DELIVERY_TYPES.find(d => d.id === o.delivery?.delivery_type);
                    const DIcon = dtype?.icon;
                    return (
                      <div key={o.id} data-testid={`pizza-order-card-${o.id}`} className="border-2 rounded-md p-3" style={{ borderColor: st.color }}>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              {DIcon && <DIcon className="w-4 h-4" style={{ color: dtype.color }} />}
                              <span className="font-semibold">{o.delivery?.customer_name || "—"}</span>
                            </div>
                            {o.delivery?.customer_phone && <div className="text-xs text-velta-mute">📞 {o.delivery.customer_phone}</div>}
                            {o.delivery?.customer_address && <div className="text-xs text-velta-mute">📍 {o.delivery.customer_address}</div>}
                            {o.delivery?.pickup_time && <div className="text-xs text-velta-mute">🕒 {o.delivery.pickup_time}</div>}
                          </div>
                          <div className="text-right">
                            <div className="font-mono font-bold">{formatEUR(o.total)}</div>
                          </div>
                        </div>
                        <div className="text-xs space-y-0.5 mb-3 border-t border-velta-border pt-2">
                          {(Array.isArray(o.lines) ? o.lines : []).slice(0, 3).map((l, idx) => (
                            <div key={idx} className="flex justify-between">
                              <span>{l.qty}× {l.name}</span>
                            </div>
                          ))}
                          {o.lines && o.lines.length > 3 && <div className="text-velta-mute">+{o.lines.length - 3} altri</div>}
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium" style={{ background: st.color + "22", color: st.color }}>
                            <StIcon className="w-3 h-3" /> {st.label}
                          </div>
                          {st.next && (
                            <button
                              data-testid={`pizza-advance-${o.id}`}
                              onClick={() => advanceStatus(o)}
                              className="ml-auto h-8 px-3 text-white rounded-md text-xs font-medium btn-tactile"
                              style={{ background: STATUS_FLOW[st.next].color }}
                            >
                              → {st.nextLabel}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showScontrino && <ScontrinoPreview order={last} tenant={tenant} fiscal onClose={() => setShowScontrino(false)} />}
    </div>
  );
}
